import React, { useCallback, useEffect, useMemo } from "react";
import ReactFlow, {
  Background,
  Edge,
  EdgeMouseHandler,
  EdgeTypes,
  Node,
  NodeTypes,
  OnConnect,
  OnEdgesChange,
  OnNodesChange,
} from "reactflow";
import GraphIssueNode, { GraphIssueNodeData } from "components/Graph/GraphIssueNode";
import GraphInfluenceEdge, {
  type GraphEdgeSelectionState,
  type GraphInfluenceEdgeData,
} from "components/Graph/GraphInfluenceEdge";
import GraphFlowControls from "components/Graph/GraphFlowControls";
import useMobileViewport from "hooks/useMobileViewport";
import { useEdgeSelection } from "components/Graph/useEdgeSelection";
import type { GraphCanvasEdgeData } from "lib/api/graphCollections";
import type { GraphLocale } from "./graphCopy";

/**
 * Props for GraphCanvas rendering.
 */
interface GraphCanvasProps {
  nodes: Array<Node<GraphIssueNodeData>>;
  edges: Array<Edge<GraphCanvasEdgeData>>;
  onNodesChange: OnNodesChange;
  onEdgesChange: OnEdgesChange;
  depth: number;
  onDepthUp: () => void;
  onDepthDown: () => void;
  /** When true, the canvas is view-only (feed cards, compact pickers, etc). */
  readOnly?: boolean;
  /**
   * When true, the canvas is rendered as a static thumbnail:
   *   - No pan/zoom/drag at all
   *   - No edge click / node selection
   *   - No controls chrome
   * Use for feed-card graph previews that should only respond to the parent's
   * click handler (to open a full-screen viewer). When set, it implies
   * `readOnly`, `showZoomControls=false`, `showDepthControls=false`, and
   * `manageEdgeSelection=false`.
   */
  presentationOnly?: boolean;
  /**
   * Controls which sides of each issue node expose handles (which in turn
   * governs the visual flow direction of edges):
   *   - "horizontal" (default for desktop editors): handles on left/right → left-to-right flow.
   *   - "vertical": handles on top/bottom → top-to-bottom flow. Recommended for
   *     feed cards so the thumbnail stacks nodes vertically and avoids the
   *     stretched left-to-right layout that made feed previews hard to read.
   *   - "auto": follows the viewport (mobile = vertical, desktop = horizontal).
   * Defaults to "auto".
   */
  nodeOrientation?: "auto" | "horizontal" | "vertical";
  /**
   * Override for React Flow's `fitViewOptions`. Useful for thumbnails that
   * want tighter padding (so only 2-3 nodes are visible at a time) or
   * explicit min/max zoom bounds.
   */
  fitViewPadding?: number;
  /**
   * When true, the canvas is in the graph-collection editor. Enables the
   * 4-side node handles and on-canvas drag-to-connect. Requires an
   * `onConnect` callback to be meaningful. Mutations (remove, note compose)
   * are NOT gated on this flag — they're controlled by the parent's handler
   * availability.
   */
  editable?: boolean;
  /**
   * Called when the user drops a drag-connect onto another node. The
   * `source`/`target` ids are React Flow node ids (= issue ids as strings).
   * Only fires when `editable` is true.
   */
  onConnect?: OnConnect;
  /** When false, hides depth controls for edit/feed surfaces. */
  showDepthControls?: boolean;
  /** When false, hides zoom/recenter/fit controls. */
  showZoomControls?: boolean;
  /**
   * Optional edge click handler. Called AFTER the internal two-step
   * selection reducer updates state, so the consumer sees the latest
   * `data.selectionState` via the next render. Parents that own the
   * selection state externally (like `GraphExplorerEdit`'s inspector sync)
   * should set `manageEdgeSelection={false}` and drive the state
   * themselves.
   */
  onEdgeClick?: EdgeMouseHandler;
  /**
   * Called when the blank pane (background) is clicked. Editors use this to
   * deselect the active edge and dismiss the edge inspector popup.
   */
  onPaneClick?: (event: React.MouseEvent) => void;
  /**
   * When true (default), GraphCanvas runs its own two-step edge-selection
   * reducer on click: first click = highlight the clicked edge's
   * neighborhood; second click inside that neighborhood = focus a single
   * edge; click on the pane = clear. Consumers that need to coordinate
   * selection with an external inspector (editor) should pass
   * `manageEdgeSelection={false}` and decorate edges with `data.selectionState`
   * themselves.
   */
  manageEdgeSelection?: boolean;
  /** Localized control labels. */
  locale?: GraphLocale;
  /**
   * React Flow node id of the graph's root / starting issue. When provided,
   * the recenter button will center the viewport over that node's midpoint
   * instead of falling back to `fitView`. Accepts `null`/`undefined` for
   * surfaces that do not have a well-defined root (e.g. the global explorer).
   */
  rootNodeId?: string | number | null;
}

const NODE_TYPES: NodeTypes = {
  issueNode: GraphIssueNode,
};

const EDGE_TYPES: EdgeTypes = {
  influenceEdge: GraphInfluenceEdge,
};

/**
 * Renders the full-screen graph canvas using React Flow. The floating control
 * cluster (zoom, recenter, fit, depth) is pinned to the bottom center on
 * desktop and to the left rail (vertically-centered) on mobile so the
 * controls remain reachable without forcing the user to scroll/swipe to
 * reveal the bottom of the viewport (which mobile browsers often hide behind
 * their dynamic chrome).
 */
const GraphCanvas: React.FC<GraphCanvasProps> = ({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  depth,
  onDepthUp,
  onDepthDown,
  readOnly = false,
  editable = false,
  onConnect,
  showDepthControls = true,
  showZoomControls = true,
  onEdgeClick,
  onPaneClick,
  manageEdgeSelection = true,
  presentationOnly = false,
  nodeOrientation = "auto",
  fitViewPadding,
  locale = "en-US",
  rootNodeId = null,
}) => {
  /*
   * Presentation-only mode collapses every interaction toggle so the
   * thumbnail is purely decorative; consumers layer their own click handler
   * on top (see `CollectionCardRefactored` card overlay). We honor it here
   * rather than pushing the logic to the parent so every graph surface goes
   * through the same gate.
   */
  const effectiveReadOnly = presentationOnly || readOnly;
  const effectiveShowZoomControls = presentationOnly ? false : showZoomControls;
  const effectiveShowDepthControls = presentationOnly ? false : showDepthControls;
  const effectiveManageEdgeSelection = presentationOnly ? false : manageEdgeSelection;
  /**
   * Viewport-based mobile detection (reactive). Deliberately NOT
   * `userIsMobile` from `UserContext` — we want this to flip on resize and
   * in dev-tools mobile emulation so the floating controls track the actual
   * layout rather than the device user-agent.
   */
  const isMobile = useMobileViewport();

  /**
   * Suppress the benign "ResizeObserver loop" warning that React Flow triggers.
   * Must use both window.addEventListener (capture phase, first) AND patch
   * window.onerror so the React dev-overlay error handler never sees it.
   */
  useEffect(() => {
    const captureHandler = (event: ErrorEvent) => {
      if (event.message?.includes("ResizeObserver")) {
        event.stopImmediatePropagation();
        event.preventDefault();
      }
    };
    window.addEventListener("error", captureHandler, true);

    const prevOnError = window.onerror;
    window.onerror = (message, ...args) => {
      if (typeof message === "string" && message.includes("ResizeObserver")) {
        return true;
      }
      if (typeof prevOnError === "function") {
        return (prevOnError as (...params: unknown[]) => boolean)(message, ...args);
      }
      return false;
    };

    return () => {
      window.removeEventListener("error", captureHandler, true);
      window.onerror = prevOnError;
    };
  }, []);

  const hasAnyControls = effectiveShowZoomControls || effectiveShowDepthControls;
  const normalizedRootNodeId =
    rootNodeId == null ? undefined : String(rootNodeId);

  /*
   * Resolve node orientation for handle placement. `GraphIssueNode` reads
   * `data.isMobile` to flip between horizontal (left/right handles) and
   * vertical (top/bottom handles) layouts. For `nodeOrientation="auto"` we
   * leave each node's existing `isMobile` value alone so editor-driven
   * decoration (viewport-based) still wins. For explicit horizontal /
   * vertical we override every node's `isMobile` flag uniformly.
   */
  const shouldForceNodeOrientation =
    nodeOrientation === "horizontal" || nodeOrientation === "vertical";
  const orientedNodes = useMemo(() => {
    if (!shouldForceNodeOrientation) {
      return nodes;
    }
    const forceIsMobile = nodeOrientation === "vertical";
    return nodes.map((node) => ({
      ...node,
      data: {
        ...(node.data || {}),
        isMobile: forceIsMobile,
      },
    }));
  }, [nodes, nodeOrientation, shouldForceNodeOrientation]);

  /**
   * Mobile pins the controls to the left edge, vertically centered, so users
   * (including left-handed users) can operate them without swiping to uncover
   * the bottom of the canvas. Desktop keeps the familiar bottom-center bar.
   */
  const controlsWrapperClassName = isMobile
    ? "graph-canvas-controls-wrapper absolute left-3 top-1/2 -translate-y-1/2 z-10"
    : "graph-canvas-controls-wrapper absolute bottom-4 left-1/2 -translate-x-1/2 z-10";

  /*
   * Internal two-step edge-selection reducer. Enabled by default so every
   * GraphCanvas surface (feed cards, explore view, draft editor, compact
   * pickers) gets the same click-to-trace / click-to-focus / click-canvas-
   * to-clear interaction. The editor turns this off (`manageEdgeSelection=false`)
   * because it needs to drive the selection from its own inspector state.
   * Presentation-only thumbnails also skip this reducer.
   */
  const {
    selectionByEdgeId: internalSelectionByEdgeId,
    handleEdgeClick: internalHandleEdgeClick,
    clear: internalClearSelection,
  } = useEdgeSelection(effectiveManageEdgeSelection ? edges : []);

  const decoratedEdges = useMemo<Array<Edge<GraphInfluenceEdgeData>>>(() => {
    if (!effectiveManageEdgeSelection) {
      return edges as Array<Edge<GraphInfluenceEdgeData>>;
    }
    return edges.map((edge) => {
      const selectionState: GraphEdgeSelectionState =
        internalSelectionByEdgeId.get(String(edge.id)) ?? "none";
      return {
        ...edge,
        animated: selectionState === "focus" || Boolean(edge.animated),
        data: {
          ...(edge.data || {}),
          selectionState,
          locale,
        },
      } as Edge<GraphInfluenceEdgeData>;
    });
  }, [edges, internalSelectionByEdgeId, locale, effectiveManageEdgeSelection]);

  const handleEdgeClickInternal = useCallback<EdgeMouseHandler>(
    (event, edge) => {
      if (effectiveManageEdgeSelection) {
        internalHandleEdgeClick(edge);
      }
      if (onEdgeClick) {
        onEdgeClick(event, edge);
      }
    },
    [internalHandleEdgeClick, effectiveManageEdgeSelection, onEdgeClick]
  );

  const handlePaneClickInternal = useCallback(
    (event: React.MouseEvent) => {
      if (effectiveManageEdgeSelection) {
        internalClearSelection();
      }
      if (onPaneClick) {
        onPaneClick(event);
      }
    },
    [internalClearSelection, effectiveManageEdgeSelection, onPaneClick]
  );

  /*
   * Resolved `fitViewOptions`. Thumbnails benefit from a slightly larger
   * padding + a floor on zoom so node avatars read at a glance. Editors
   * keep the prior tight fit so the initial load shows the whole graph.
   */
  const resolvedFitViewOptions = useMemo(() => {
    if (typeof fitViewPadding === "number") {
      return { padding: fitViewPadding, minZoom: presentationOnly ? 0.6 : undefined };
    }
    return presentationOnly
      ? { padding: 0.8, minZoom: 0.6, maxZoom: 1.6 }
      : { padding: 0.2 };
  }, [fitViewPadding, presentationOnly]);

  return (
    <div
      className={`graph-canvas-root relative h-full min-h-[320px] w-full overflow-hidden bg-eoe-bg dark:bg-eoe-dark-bg${
        presentationOnly ? " graph-canvas-presentation-only" : ""
      }`}
    >
      <ReactFlow
        nodes={orientedNodes}
        edges={decoratedEdges}
        nodeTypes={NODE_TYPES}
        edgeTypes={EDGE_TYPES}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onEdgeClick={presentationOnly ? undefined : handleEdgeClickInternal}
        onPaneClick={presentationOnly ? undefined : handlePaneClickInternal}
        onConnect={editable && !presentationOnly ? onConnect : undefined}
        fitView={true}
        fitViewOptions={resolvedFitViewOptions}
        nodesDraggable={!effectiveReadOnly && !presentationOnly}
        nodesConnectable={editable && !effectiveReadOnly && !presentationOnly}
        /*
         * Always keep edges clickable in interactive surfaces (editor, feed
         * card in-place highlight, explore). Thumbnails set
         * `elementsSelectable=false` so the parent's overlay click handler
         * (opens the full-screen viewer) gets every tap/click cleanly.
         */
        elementsSelectable={!presentationOnly}
        panOnScroll={!presentationOnly}
        panOnDrag={!presentationOnly}
        zoomOnScroll={!presentationOnly}
        zoomOnPinch={!presentationOnly}
        zoomOnDoubleClick={!presentationOnly}
        preventScrolling={!presentationOnly}
        minZoom={0.2}
        maxZoom={2}
        defaultEdgeOptions={{
          /*
           * All graph surfaces render influence links through the custom
           * bezier edge (badges, two-step highlight, animated focus).
           * Keeping one edge type across editor/feed/explore surfaces is
           * what lets the feature toggles (`editable`,
           * `manageEdgeSelection`) stay orthogonal from the rendering
           * pipeline.
           */
          type: "influenceEdge",
          style: { strokeWidth: 2 },
        }}
        className="graph-canvas-flow"
        style={{ width: "100%", height: "100%" }}
      >
        <Background gap={24} size={1} color="#9CA3AF" />
        {hasAnyControls ? (
          <div className={controlsWrapperClassName}>
            <GraphFlowControls
              depth={depth}
              onDepthUp={onDepthUp}
              onDepthDown={onDepthDown}
              minDepth={0}
              maxDepth={5}
              showDepthControls={showDepthControls}
              locale={locale}
              orientation={isMobile ? "vertical" : "horizontal"}
              rootNodeId={normalizedRootNodeId}
            />
          </div>
        ) : null}
      </ReactFlow>
    </div>
  );
};

export default GraphCanvas;
